import React, { useState, useEffect } from "react";
import { getAccessToken, isAuthenticated } from "../utils/jwt";
import { MdOutlineDelete } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { CATEGORIES } from "../constants";

const EditNameModal = ({ isOpen, onClose, origName, onUpdate }) => {
  const [formData, setFormData] = useState({
    eng_word: "",
    arb_word: "",
    syr_word: "",
    gender: "",
    category: [],
    meaning: "",
    eng_desc: "",
    arb_desc: "",
    variations: "",
    src: "",
    status: "pending",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (origName) {
      setFormData({
        eng_word: origName.eng_word || "",
        arb_word: origName.arb_word || "",
        syr_word: origName.syr_word || "",
        gender: origName.gender || "",
        category: Array.isArray(origName.category) ? origName.category : [],
        meaning: origName.meaning || "",
        eng_desc: origName.eng_desc || "",
        arb_desc: origName.arb_desc || "",
        variations: origName.variations || "",
        src: origName.src || "",
        status: origName.status,
      });
    }
  }, [origName]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const category = formData.category;
      if (e.target.checked) {
        setFormData({ ...formData, category: [...category, value] });
      } else {
        setFormData({
          ...formData,
          category: category.filter((c) => c !== value),
        });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const accessToken = getAccessToken();
      const response = await fetch(
        `/api/names/update-name?id=${origName._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            eng_word: formData.eng_word,
            arb_word: formData.arb_word,
            syr_word: formData.syr_word,
            meaning: formData.meaning,
            arb_meaning: formData.arb_meaning,
            gender: formData.gender,
            category: formData.category,
            origin: formData.origin,
            status: formData.status,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to update name");
      }

      onUpdate?.();
      onClose();
    } catch (err) {
      setError(err.message || "An error occurred while updating the name");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setError("");
    setIsLoading(true);

    try {
      if (!isAuthenticated()) {
        throw new Error("Authentication required");
      }

      const accessToken = getAccessToken();
      const response = await fetch(
        `/api/names/delete-name?id=${origName._id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to delete name");
      }

      onClose();
    } catch (err) {
      setError(err.message || "Failed to delete name");
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-box modal-margins" style={{ width: "800px" }}>
        <button className="popup-close" onClick={onClose}>
          &times;
        </button>
        <h2>Edit {origName.eng_word}</h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: "12px" }}>
            <div>
              <label htmlFor="eng_word">English Name:</label>
              <input
                id="eng_word"
                name="eng_word"
                type="text"
                value={formData.eng_word}
                onChange={handleChange}
                required
                style={{
                  width: "50%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label htmlFor="arb_word">Arabic Name:</label>
              <input
                id="arb_word"
                name="arb_word"
                type="text"
                value={formData.arb_word}
                onChange={handleChange}
                required
                style={{
                  width: "50%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label htmlFor="syr_word">Syriac Name:</label>
              <input
                id="syr_word"
                name="syr_word"
                type="text"
                value={formData.syr_word}
                onChange={handleChange}
                required
                style={{
                  width: "50%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label>Gender:</label>
              <div style={{ display: "flex", gap: "12px", marginTop: 4 }}>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="male"
                    checked={formData.gender === "male"}
                    onChange={handleChange}
                  />
                  Male
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="female"
                    checked={formData.gender === "female"}
                    onChange={handleChange}
                  />
                  Female
                </label>
                <label>
                  <input
                    type="radio"
                    name="gender"
                    value="neutral"
                    checked={formData.gender === "neutral"}
                    onChange={handleChange}
                  />
                  Neutral
                </label>
              </div>
            </div>
            <div>
              <label>Categories:</label>
              <div style={{ display: "flex", gap: "12px", marginTop: 4 }}>
                {CATEGORIES.map((cat) => (
                  <label key={cat}>
                    <input
                      type="checkbox"
                      value={cat}
                      checked={formData.category.includes(cat)}
                      onChange={handleChange}
                    />
                    {cat}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="meaning">Meaning:</label>
              <input
                id="meaning"
                name="meaning"
                type="text"
                value={formData.meaning}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label htmlFor="arb_desc">Arabic Description:</label>
              <input
                type="text"
                id="arb_desc"
                name="arb_desc"
                value={formData.arb_desc}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label htmlFor="src">Source:</label>
              <input
                id="src"
                name="src"
                type="text"
                value={formData.src}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "0.5rem",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  marginTop: 4,
                }}
              />
            </div>
            <div>
              <label
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <input
                  type="checkbox"
                  checked={formData.status === "approved"}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      status: e.target.checked ? "approved" : "pending",
                    });
                  }}
                />
                Approved
              </label>
            </div>
            {error && (
              <div style={{ color: "#e11d48", marginBottom: 12 }}>{error}</div>
            )}
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: 20 }}>
            <button
              type="submit"
              className="reset-btn email-modal-send-btn"
              style={{ flex: 1 }}
              disabled={isLoading || isSubmitting}
            >
              {isSubmitting ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <div className="button-spinner"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <FaCheckCircle size={20} />
                  Save Changes
                </span>
              )}
            </button>
            <button
              type="button"
              className="reset-btn"
              style={{
                flex: 1,
                backgroundColor: "#e11d48",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
            >
              <MdOutlineDelete size={20} />
              Delete
            </button>
          </div>
        </form>

        {showDeleteConfirm && (
          <div className="popup-overlay" style={{ zIndex: 1001 }}>
            <div className="popup-box" style={{ maxWidth: "400px" }}>
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete this name? This action cannot be
                undone.
              </p>
              <div style={{ display: "flex", gap: "10px", marginTop: 20 }}>
                <button
                  className="reset-btn"
                  style={{ flex: 1 }}
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="reset-btn"
                  style={{
                    flex: 1,
                    backgroundColor: "#e11d48",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                  onClick={handleDelete}
                  disabled={isLoading}
                >
                  <MdOutlineDelete size={20} />
                  {isLoading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditNameModal;
